import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface ActivityUser {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
}

export interface ActivityEntry {
  id: string;
  projectId: string;
  taskId: string | null;
  userId: string;
  activityType: string;
  description: string;
  changes: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  user: ActivityUser;
}

interface ActivityResponse {
  activities: ActivityEntry[];
  nextCursor: string | null;
  hasMore: boolean;
}

export const activityKeys = {
  all: ['activity'] as const,
  project: (projectId: string) => [...activityKeys.all, projectId] as const,
};

export function useProjectActivity(projectId: string, options?: { activityType?: string; taskId?: string }) {
  return useInfiniteQuery({
    queryKey: [...activityKeys.project(projectId), options],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.append('limit', '30');
      if (pageParam) params.append('cursor', pageParam);
      if (options?.activityType) params.append('activityType', options.activityType);
      if (options?.taskId) params.append('taskId', options.taskId);

      const response = await api.get<ActivityResponse>(
        `/projects/${projectId}/activity?${params.toString()}`
      );
      return response.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: !!projectId,
  });
}
